import React from 'react';
import { connect } from 'react-redux';
import * as PropTypes from "prop-types";
import { withRouter } from 'react-router-dom';
import { logContentCardImpressions } from '@braze/web-sdk';
import { isBrazeReady } from '../../../../../shared/utilities/brazeReady';
import { ContentCardBase } from "../ContentCardBase";
import styles from './HeroBannerIntro.module.css';
import elementConstant from "../../../../../shared/constants/element";
import * as brazeActions from '../../../../../redux/actions/braze';
import * as elementsActions from "../../../../../redux/actions/elements";
import * as contentCardConstants from "../../../../../shared/constants/contentCards";

class HeroBannerIntro extends ContentCardBase {
  constructor(props) {
    super(props);
    const { introHeroBanner } = props;
    const { extras } = introHeroBanner;
    const { desktopVideoUrl } = extras;

    this.state = {
      videoUrl: desktopVideoUrl,
      fadeOutClass: null,
    };

    this.videoElRef = React.createRef();
  }

  componentDidMount() {
    this.updateVideo();
    const { contentCardRefreshDelay, introHeroBanner } = this.props;
    
    // Add impression tracking
    if (introHeroBanner && Object.keys(introHeroBanner).length > 0 && isBrazeReady()) {
      logContentCardImpressions([introHeroBanner], true);
    }
    
    this.timeOut = setTimeout(() => {
      this.setState({ fadeOutClass: styles.fadeOut });
    }, parseInt(contentCardRefreshDelay, 10) - elementConstant.introBannerReduceTimeoutOffset);
  }

  componentWillUnmount() {
    if (this.timeOut) {
      clearTimeout(this.timeOut);
    }
  }

  updateVideo = () => {
    const { introHeroBanner } = this.props;
    const { extras } = introHeroBanner;
    const { desktopVideoUrl, mobileVideoUrl } = extras;
    const videoUrl = window.innerWidth <= elementConstant.introHeroBannerWidthThreshold 
      ? mobileVideoUrl 
      : desktopVideoUrl;
    this.setState({ videoUrl });
  }

  redirectTo = (cardData) => {
    const { location, setHeroContentCardData } = this.props;
    
    // Support both 'url' and 'u' (Braze SDK may use either)
    const url = cardData.url || cardData.u;

    // Guard against undefined url - only navigate if url exists and is valid
    if (!url) {
      return;
    }

    if(location?.pathname !== '/' || url.startsWith(contentCardConstants.EXTERNAL_URL_PREFIX)) {
      this.handleClick(cardData)
    } else {
      // Homepage + internal URL path
      setHeroContentCardData({contentCardHandleClick: this.handleClick, contentCardData: cardData});
      // Don't call handleClick here - let the widget handle navigation after user selects option
    }
  }

  render() {
    const { fadeOutClass, videoUrl } = this.state;
    const { introHeroBanner } = this.props;
    const { extras, url } = introHeroBanner;
    const { desktopVideoUrl, mobileVideoUrl, mime } = extras;

    const hasVideo = desktopVideoUrl || mobileVideoUrl;
    const videoError = this.state.videoError || false;
    const showVideo = hasVideo && !videoError;
    const Container = url ? "button" : "div";

    return (
      <Container
        type={url ? "button" : undefined}
        className={[styles.videoContainer, fadeOutClass].join(' ')}
        onClick={() => {
          if (url) {
            this.redirectTo(introHeroBanner);
          }
        }}
        onKeyDown={(e) => {
          if (url && e.key === 'Enter') {
            this.handleClick(introHeroBanner);
          }
        }}
        aria-label={url ? 'Click to navigate to content' : undefined}
      >
        {showVideo && (
          /* eslint-disable-next-line jsx-a11y/media-has-caption */
          <video
            autoPlay
            muted
            loop
            playsInline
            webkit-playsinline="webkit-playsinline"
            aria-hidden="true"
            tabIndex="-1"
            className={url ? "cursor-pointer" : undefined}
            onClick={(e) => {
              if (url) {
                e.stopPropagation();
                this.redirectTo(introHeroBanner);
              }
            }}
            onKeyDown={(e) => {
              if (url && (e.key === 'Enter' || e.key === ' ')) {
                e.stopPropagation();
                this.redirectTo(introHeroBanner);
              }
            }}
            ref={this.videoElRef}
          >
            <source src={videoUrl} type={mime} />
          </video>
        )}
      </Container>
    );
  }
}

HeroBannerIntro.propTypes = {
  introHeroBanner: PropTypes.shape(
    {
      url: PropTypes.string,
      extras: PropTypes.shape(
        {
          template: PropTypes.string,
          desktopVideoUrl: PropTypes.string,
          mobileVideoUrl: PropTypes.string,
          mime: PropTypes.string,
        })
    }).isRequired,
  contentCardRefreshDelay: PropTypes.number.isRequired,
  setHeroContentCardData: PropTypes.func.isRequired
}

export const mapStateToProps = (state) => {
  const {
    introHeroBanner,
    contentCardRefreshDelay
  } = state.braze;

  return {
    introHeroBanner,
    contentCardRefreshDelay
  };
}

export const mapDispatchToProps = (dispatch) => ({
  contentCardSession: (value) => dispatch(brazeActions.contentCardSession(value)),
  setHeroContentCardData: (value) => dispatch(elementsActions.setHeroContentCardData(value))
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(HeroBannerIntro));
